/**
 * Error Fingerprinting Utilities
 * Generate consistent fingerprints for error grouping
 */

/**
 * Generate a fingerprint for an error
 * Used to group similar errors together in analytics
 */
export function generateErrorFingerprint(error: Error): string {
  const parts: string[] = []

  // Include error name
  parts.push(error.name || 'Error')

  // Include first line of message (normalized)
  if (error.message) {
    const firstLine = error.message.split('\n')[0]
    const normalized = normalizeErrorMessage(firstLine)
    parts.push(normalized)
  }

  // Include first meaningful stack frame
  if (error.stack) {
    const frame = extractFirstFrame(error.stack)
    if (frame) {
      parts.push(frame)
    }
  }

  // Generate hash from parts
  return hashParts(parts)
}

/**
 * Normalize error message by removing variable parts
 */
function normalizeErrorMessage(message: string): string {
  return message
    // Remove line/column numbers
    .replace(/:\d+:\d+/g, ':X:X')
    // Remove UUIDs
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, 'UUID')
    // Remove URLs
    .replace(/https?:\/\/[^\s]+/g, 'URL')
    // Remove numbers (but keep context)
    .replace(/\b\d+\b/g, 'N')
    // Remove quotes content
    .replace(/"[^"]*"/g, '"..."')
    .replace(/'[^']*'/g, "'...'")
    // Trim whitespace
    .trim()
}

/**
 * Extract first meaningful stack frame
 */
function extractFirstFrame(stack: string): string | null {
  const lines = stack.split('\n')

  for (const line of lines) {
    // Skip the error message line
    if (!line.includes('at ')) continue

    // Skip internal frames (node_modules, etc)
    if (line.includes('node_modules')) continue
    if (line.includes('<anonymous>')) continue
    if (line.includes('webpack')) continue

    // Extract meaningful part
    const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/)
    if (match) {
      const [, functionName, file] = match
      // Get just the filename without full path
      const fileName = file.split('/').pop() || file
      return `${functionName}@${fileName}`
    }

    // Try simpler format: at filename:line:col
    const simpleMatch = line.match(/at\s+(.+?):(\d+):(\d+)/)
    if (simpleMatch) {
      const [, file] = simpleMatch
      const fileName = file.split('/').pop() || file
      return `@${fileName}`
    }
  }

  return null
}

/**
 * Generate hash from string parts
 */
function hashParts(parts: string[]): string {
  const str = parts.join('|')
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }

  return Math.abs(hash).toString(16).padStart(8, '0')
}

/**
 * Create error context from Error object
 */
export function createErrorContext(error: Error): Record<string, unknown> {
  return {
    name: error.name,
    message: error.message,
    stack: error.stack?.substring(0, 2000),
  }
}

/**
 * Extract component name from React component stack
 */
export function extractComponentName(componentStack: string | undefined): string | undefined {
  if (!componentStack) return undefined

  // React component stacks look like:
  // \n    at ComponentName (url)\n    at ParentComponent (url)
  const lines = componentStack.split('\n').filter((l) => l.trim())

  for (const line of lines) {
    const match = line.match(/at\s+([A-Z][a-zA-Z0-9]*)\s/)
    if (match) {
      return match[1]
    }
  }

  return undefined
}
